// Зарплатный контур Хронума: месяц сводки, состояние передачи ведомости в оплату и текст
// итога синхронизации. Чистые функции без импортов через `@…` — их проверяет
// `npm run test:financier` (model.test.mjs рядом).

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

/** Первое число месяца даты: «2026-11-03» → «2026-11-01» — так месяц уходит на сервер. */
export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** Месяц даты и `count − 1` предыдущих (первые числа) — для выбора месяца на экране. */
export function recentMonths(iso: string, count: number): string[] {
  let year = Number(iso.slice(0, 4));
  let month = Number(iso.slice(5, 7));
  const out: string[] = [];
  for (let i = 0; i < count; i += 1) {
    out.push(`${year}-${String(month).padStart(2, '0')}-01`);
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }
  return out;
}

/** Месяц словами: «ноябрь 2026». */
export function monthLabel(iso: string): string {
  return `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;
}

/** То же с заглавной буквы — для списка выбора и заголовков. */
export function monthTitle(iso: string): string {
  const label = monthLabel(iso);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Что показать в колонке действий ведомости:
 * - `in_registry` — заявка на оплату уже есть;
 * - `enqueue` — ведомость сверена, заявки нет, можно передать в оплату;
 * - `not_enqueued` — то же, но у пользователя нет права записи: ведомость не передана;
 * - `none` — передавать нечего.
 * Статус — по факту (есть ли заявка), право записи влияет только на кнопку.
 */
export type BatchAction = 'enqueue' | 'not_enqueued' | 'in_registry' | 'none';

export function batchAction(
  batch: { status: string; payment_request_id: string | null },
  canWrite: boolean,
): BatchAction {
  if (batch.payment_request_id) return 'in_registry';
  if (batch.status !== 'verified_chronum') return 'none';
  return canWrite ? 'enqueue' : 'not_enqueued';
}

export interface SyncResultLike {
  is_live_external: boolean;
  period_month: string;
  sites_updated_count: number;
  total_fot_rub: number;
  total_workers: number;
  failed_sites: { chronum_site_id: string; chronum_site_name: string; reason: string }[];
  missing_site_ids: string[];
  warnings: string[];
}

/**
 * Итог синхронизации для баннера. Частичный результат так и называется: какие объекты не
 * записаны и что делать. `formatAmount` — формат суммы экрана (млн ₽).
 */
export function syncResultText(
  res: SyncResultLike,
  formatAmount: (n: number) => string,
): { text: string; tone: 'success' | 'warning' } {
  const month = monthLabel(res.period_month);
  const parts: string[] = [];
  let tone: 'success' | 'warning' = 'success';

  if (res.failed_sites.length > 0) {
    tone = 'warning';
    const names = res.failed_sites
      .map((f) => f.chronum_site_name || f.chronum_site_id)
      .join(', ');
    parts.push(
      `Синхронизация за ${month} прошла частично: записано объектов — ${res.sites_updated_count}. ` +
        `Не записаны: ${names}. ${res.failed_sites[0].reason}`,
    );
  } else if (res.sites_updated_count === 0) {
    tone = 'warning';
    parts.push(`За ${month} обновлять нечего: данных из Хронума за этот месяц нет.`);
  } else {
    const source = res.is_live_external ? 'по данным Хронума' : 'пересчёт по сменам';
    parts.push(
      `Синхронизация за ${month} завершена (${source}): обновлено объектов — ${res.sites_updated_count}, ` +
        `сотрудников — ${res.total_workers}, ФОТ — ${formatAmount(res.total_fot_rub)}.`,
    );
  }
  if (res.missing_site_ids.length > 0 && res.sites_updated_count + res.failed_sites.length > 0) {
    parts.push(
      `У объектов Хронума без строк за ${month} (${res.missing_site_ids.length}) начисления не загружены.`,
    );
  }
  if (res.warnings.length > 0) {
    tone = 'warning';
    parts.push(...res.warnings);
  }
  return { text: parts.join(' '), tone };
}
